import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Column, Row, Spinner, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { formatPaymentDetailsList, getStatusLabel, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { useLocaleKeys } from '~/i18n';
import { invoiceSummaryModel } from '~/models';
import { useClientBilling } from '../../../../hooks/useClientBilling';
import { SectionCard } from '../SectionCard/SectionCard';
import styles from './BillingOverviewCard.module.scss';
import { InvoiceLine } from './InvoiceLine/InvoiceLine';
export const GetBillingOverviewCardTID = (parentTestId)=>getTestIds(parentTestId);
export function BillingOverviewCard(param) {
    let { customerId, onManageBilling, onBillClient, onViewUpcomingInvoice, onViewAllOverdue } = param;
    const { billing, isLoading } = useClientBilling(customerId);
    const { upcomingInvoice, latestInvoice, overdueInvoices } = billing;
    const { BillingOverviewCardTestIds: TID } = useMemo(()=>GetBillingOverviewCardTID(), []);
    const { t: texts } = useLocaleKeys('pages.clientDetail.summary.billingOverview');
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const topOverdue = overdueInvoices[0];
    const overdueReason = topOverdue ? formatPaymentDetailsList(invoiceSummaryModel.paymentDetails(topOverdue), invoiceLogicTexts.paymentDetailLabels, invoiceLogicTexts.labelOnDate) : '';
    return /*#__PURE__*/ _jsx(SectionCard, {
        bi: {
            name: 'client_detail_billing_overview_card'
        },
        testId: TID.Root(),
        title: texts.title(),
        actions: /*#__PURE__*/ _jsxs(Row, {
            gap: 12,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'client_detail_manage_billing'
                    },
                    testId: TID.ManageBillingButton(),
                    skin: "primary",
                    underline: "none",
                    onClick: onManageBilling,
                    children: texts.manageBilling()
                }),
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.actionSeparator
                }),
                /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'client_detail_billing_bill_client'
                    },
                    testId: TID.BillClientButton(),
                    skin: "primary",
                    underline: "none",
                    onClick: onBillClient,
                    children: texts.billClient()
                })
            ]
        }),
        children: isLoading ? /*#__PURE__*/ _jsx(Box, {
            className: styles.loader,
            children: /*#__PURE__*/ _jsx(Spinner, {})
        }) : /*#__PURE__*/ _jsxs(Column, {
            gap: 20,
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "bold",
                            variant: "paragraph2",
                            children: texts.upcomingInvoiceTitle()
                        }),
                        upcomingInvoice == null ? /*#__PURE__*/ _jsx(Typography, {
                            testId: TID.EmptyUpcoming(),
                            skin: "secondary",
                            children: texts.upcomingInvoiceEmpty()
                        }) : /*#__PURE__*/ _jsx(Box, {
                            testId: TID.UpcomingInvoiceRow(),
                            className: styles.invoiceRow,
                            children: /*#__PURE__*/ _jsxs(Column, {
                                gap: 8,
                                children: [
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 12,
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                children: texts.toBeIssuedOn({
                                                    date: upcomingInvoice.issueDate()
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                weight: "bold",
                                                children: upcomingInvoice.total()
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(TextButton, {
                                        bi: {
                                            name: 'client_detail_view_upcoming_invoice'
                                        },
                                        testId: TID.UpcomingViewDetailsLink(),
                                        skin: "primary",
                                        underline: "none",
                                        onClick: onViewUpcomingInvoice,
                                        children: texts.viewDetails()
                                    })
                                ]
                            })
                        })
                    ]
                }),
                latestInvoice != null && /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "bold",
                            variant: "paragraph2",
                            children: texts.latestInvoiceTitle()
                        }),
                        /*#__PURE__*/ _jsx(InvoiceLine, {
                            invoice: latestInvoice,
                            customerId: customerId,
                            testId: TID.LatestInvoiceRow(),
                            statusLabel: getStatusLabel(latestInvoice.displayStatusV2, invoiceLogicTexts.displayedStatusLabels),
                            showDivider: true
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            alignItems: "center",
                            justifyContent: "space-between",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    weight: "bold",
                                    variant: "paragraph2",
                                    children: texts.overdueInvoicesTitle()
                                }),
                                overdueInvoices.length > 1 && /*#__PURE__*/ _jsx(TextButton, {
                                    bi: {
                                        name: 'client_detail_overdue_view_all'
                                    },
                                    skin: "primary",
                                    underline: "none",
                                    onClick: onViewAllOverdue,
                                    children: texts.viewAll()
                                })
                            ]
                        }),
                        topOverdue == null ? /*#__PURE__*/ _jsx(Typography, {
                            testId: TID.EmptyOverdue(),
                            skin: "secondary",
                            children: texts.overdueInvoicesEmpty()
                        }) : /*#__PURE__*/ _jsx(InvoiceLine, {
                            invoice: topOverdue,
                            customerId: customerId,
                            testId: TID.OverdueInvoiceRow(topOverdue.id),
                            statusLabel: overdueReason || texts.missingPaymentMethod(),
                            showDivider: true
                        })
                    ]
                })
            ]
        })
    });
}
